import { types } from "mobx-state-tree";
import { flow } from "modules/common/models/flow";
import { Notificator } from "modules/common/models/notificator";
import { Queryable } from "modules/common/models/queryable";
import { Transport } from "modules/common/models/transport";
import { apiUrls } from "modules/common/services/communication/urls";
import {
    buildRows,
    EmployeeRow,
    PeriodCode,
    periodRate,
    quarterMargins,
    ReasonType,
    roleStats,
    totals,
    UnitBenchmarks,
    UnitEconomicsData,
    UnitReason,
    UnitRole,
    YEAR_PERIOD,
} from "./calc";

export type UnitTab = "economics" | "quarters";
export type UnitSegment = UnitRole | "all";

/** Период по умолчанию: последний закрытый квартал текущего года, для прошлых лет — весь год */
export function defaultPeriod(year: number, now = new Date()): PeriodCode {
    if (year < now.getFullYear()) {
        return YEAR_PERIOD;
    }

    const current = Math.floor(now.getMonth() / 3) + 1;
    return (current > 1 ? current - 1 : current) as PeriodCode;
}

export const UnitEconomicsStore = types
    .compose(
        Transport,
        Notificator,
        Queryable,
        types.model({
            year: types.number,
            period: types.number,
            tab: types.string,
            segment: types.string,
            department: types.string,
            onlyAnomalies: types.boolean,
            /** Группировать таблицу по отделам */
            groupByDepartment: types.optional(types.boolean, false),
            /** Шапка с карточками показателей свёрнута — в таблицу помещается больше сотрудников */
            headerCollapsed: types.optional(types.boolean, false),
            loader: types.number,
            data: types.frozen<UnitEconomicsData | null>(null),
        })
    )
    .views((self) => ({
        get isLoading() {
            return self.loader > 0;
        },

        get periodCode() {
            return self.period as PeriodCode;
        },

        /** Все строки периода без фильтров — для итогов и бенчмарков */
        get periodRows(): EmployeeRow[] {
            return buildRows(self.data, self.period as PeriodCode);
        },
    }))
    .views((self) => ({
        get departments() {
            return Array.from(new Set(self.periodRows.map((r) => r.department).filter(Boolean))).sort((a, b) =>
                a.localeCompare(b)
            );
        },

        /** Строки с учётом сегмента, отдела и поиска — без фильтра аномалий */
        get visibleRows(): EmployeeRow[] {
            const query = self.pureQuery;
            return self.periodRows.filter((row) => {
                if (self.segment !== "all" && row.role !== self.segment) {
                    return false;
                }

                if (self.department && row.department !== self.department) {
                    return false;
                }

                return !query || `${row.name} ${row.department} ${row.position}`.toLowerCase().includes(query);
            });
        },

        get totals() {
            return totals(self.periodRows);
        },

        get roleStats() {
            return self.data ? roleStats(self.periodRows, self.data.benchmarks) : [];
        },

        get overheadRate() {
            return periodRate(self.data, self.period as PeriodCode, self.periodRows);
        },

        get segmentCounts(): Record<UnitSegment, number> {
            const rows = self.periodRows;
            return {
                production: rows.filter((r) => r.role === "production").length,
                gip: rows.filter((r) => r.role === "gip").length,
                nonproduction: rows.filter((r) => r.role === "nonproduction").length,
                all: rows.length,
            };
        },

        get quarterRows() {
            const query = self.pureQuery;
            return quarterMargins(self.data).filter(
                (row) =>
                    (!self.department || row.department === self.department) &&
                    (!query || `${row.name} ${row.department}`.toLowerCase().includes(query))
            );
        },

        employee(id: string) {
            return self.data?.employees.find((e) => e.id === id) ?? null;
        },
    }))
    .views((self) => ({
        /** Строки таблицы: видимые с учётом фильтра аномалий */
        get rows(): EmployeeRow[] {
            return self.onlyAnomalies ? self.visibleRows.filter((r) => r.anomaly) : self.visibleRows;
        },

        /** Отклонения среди того, что сейчас видно в таблице, — столько строк и останется по клику */
        get anomalyCount() {
            return self.visibleRows.filter((r) => r.anomaly).length;
        },
    }))
    .actions((self) => ({
        setPeriod(period: PeriodCode) {
            self.period = period;
        },

        setTab(tab: UnitTab) {
            self.tab = tab;
        },

        setSegment(segment: UnitSegment) {
            self.segment = segment;
        },

        setDepartment(department: string) {
            self.department = department;
        },

        toggleAnomalies() {
            self.onlyAnomalies = !self.onlyAnomalies;
        },

        setGroupByDepartment(value: boolean) {
            self.groupByDepartment = value;
        },

        toggleHeader() {
            self.headerCollapsed = !self.headerCollapsed;
        },

        /** Сбросить сужающие фильтры (поиск сбрасывает страница — он общий для табельных экранов) */
        resetFilters() {
            self.onlyAnomalies = false;
            self.department = "";
            self.segment = "all";
        },

        replaceReason(employerId: string, year: number, quarter: number, reason: UnitReason | null) {
            if (!self.data) {
                return;
            }

            const rest = self.data.reasons.filter(
                (r) => !(r.employerId === employerId && r.year === year && r.quarter === quarter)
            );

            self.data = { ...self.data, reasons: reason ? [...rest, reason] : rest };
        },

        replaceBenchmarks(benchmarks: UnitBenchmarks) {
            if (self.data) {
                self.data = { ...self.data, benchmarks };
            }
        },

        startLoading() {
            self.loader++;
        },

        stopLoading() {
            self.loader = Math.max(0, self.loader - 1);
        },

        applyData(data: UnitEconomicsData, year: number, keepPeriod: boolean) {
            self.data = data;
            if (!keepPeriod || self.year !== year) {
                self.period = defaultPeriod(year);
            }
            self.year = year;
        },
    }))
    .actions((self) => ({
        load: flow(function* (year?: number) {
            const target = year ?? self.year;
            self.startLoading();

            try {
                const data: UnitEconomicsData = yield self.transport.get<UnitEconomicsData>(
                    apiUrls.unitEconomics.list,
                    {
                        params: { year: target },
                    }
                );

                self.applyData(data, target, !!self.data);
                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            } finally {
                self.stopLoading();
            }
        }),

        saveReason: flow(function* (employerId: string, reasons: ReasonType[], comment: string) {
            const quarter = self.period;
            const year = self.year;

            try {
                const saved: UnitReason | null = yield self.transport.post<UnitReason | null>(
                    apiUrls.unitEconomics.reason,
                    { employerId, year, quarter, reasons, comment }
                );

                self.replaceReason(employerId, year, quarter, saved || null);
                self.notify.success(saved ? "Причина отклонения сохранена" : "Причина отклонения снята");
                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            }
        }),

        saveBenchmarks: flow(function* (benchmarks: UnitBenchmarks) {
            try {
                const saved: UnitBenchmarks = yield self.transport.post<UnitBenchmarks>(
                    apiUrls.unitEconomics.benchmarks,
                    benchmarks
                );

                self.replaceBenchmarks(saved);
                self.notify.success("Целевая загрузка сохранена");
                return true;
            } catch (er) {
                self.notify.error(er);
                return false;
            }
        }),
    }));

export type UnitEconomicsStoreType = typeof UnitEconomicsStore.Type;
export type UnitEconomicsStoreSnapshotType = typeof UnitEconomicsStore.SnapshotType;

export const initialState = (): UnitEconomicsStoreSnapshotType => {
    const year = new Date().getFullYear();
    return {
        year,
        period: defaultPeriod(year),
        tab: "economics",
        segment: "production",
        department: "",
        onlyAnomalies: false,
        groupByDepartment: false,
        headerCollapsed: false,
        loader: 0,
        data: null,
        query: "",
        pureQuery: "",
    };
};
